import React, { useState } from 'react';
import { createParentCase, createChildCase } from '../services/casesService';
import imageCompression from 'browser-image-compression';
import './CaseFormNew.css';

/* ===== Helpers ===== */
const isVideoFile = (file) => {
  if (file.type?.startsWith('video/')) return true;
  const ext = file.name?.split('.').pop()?.toLowerCase();
  return ['mp4', 'mov', 'webm', 'mkv'].includes(ext);
};

const MAX_IMAGE_MB = 6;

const CaseFormNew = ({ parentId = null, onCreated, onCancel }) => {
  const [formData, setFormData] = useState({
    client_name: '',
    description: '',
    images: [],
    videos: [],
  });

  const [loading, setLoading] = useState(false);

  /* ===== Inputs texto ===== */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ===== Files (images + videos) ===== */
  const handleFileChange = async (e) => {
    const { name, files } = e.target;
    if (!files?.length) return;

    const processedFiles = [];

    for (const file of Array.from(files)) {

      // 🔒 FIX Drive / Mobile → clonar archivo
      const buffer = await file.arrayBuffer();
      const safeFile = new File(
        [buffer],
        file.name || `file-${Date.now()}`,
        { type: file.type || '' }
      );

      // 🎥 VIDEO
      if (isVideoFile(safeFile)) {
        processedFiles.push(safeFile);
        continue;
      }

      // 🖼️ IMAGEN
      if (safeFile.type.startsWith('image/')) {

        // ≤ 6 MB → directo
        if (safeFile.size <= MAX_IMAGE_MB * 1024 * 1024) {
          processedFiles.push(safeFile);
          continue;
        }

        // > 6 MB → comprimir
        try {
          const compressed = await imageCompression(safeFile, {
            maxSizeMB: MAX_IMAGE_MB,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });

          processedFiles.push(
            new File([compressed], safeFile.name, {
              type: compressed.type,
            })
          );
        } catch (err) {
          console.warn('Compresión falló, se sube original', err);
          processedFiles.push(safeFile);
        }

        continue;
      }

      // 🧯 Fallback (no perder archivo)
      console.warn('Archivo sin tipo claro, se envía igual:', safeFile.name);
      processedFiles.push(safeFile);
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedFiles,
    }));
  };

  /* ===== Submit ===== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.client_name.trim()) {
      alert('El nombre del cliente es obligatorio');
      return;
    }

    const data = new FormData();
    data.append('client_name', formData.client_name);
    data.append('description', formData.description);

    formData.images.forEach(img => {
      data.append('images[]', img);
    });

    formData.videos.forEach(vid => {
      data.append('videos[]', vid);
    });

    try {
      setLoading(true);

      if (parentId) {
        await createChildCase(parentId, data);
      } else {
        await createParentCase(data);
      }

      // reset
      setFormData({
        client_name: '',
        description: '',
        images: [],
        videos: [],
      });

      if (onCreated) onCreated();

    } catch (error) {
      console.error('Error creando caso:', error);
      alert('Error al crear el caso');
    } finally {
      setLoading(false);
    }
  };

  /* ===== Render ===== */
  return (
    <div className="case-form-new">
      <div className="form-header">
        <h3>{parentId ? 'Crear Caso Hijo' : 'Crear Caso Padre'}</h3>
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn-cancel">×</button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="case-form">
        <div className="form-group">
          <label>Nombre del Cliente *</label>
          <input
            type="text"
            name="client_name"
            value={formData.client_name}
            onChange={handleInputChange}
            required
            placeholder="Ej: Boda en la Rural"
          />
        </div>

        <div className="form-group">
          <label>Descripción</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
            placeholder="Describe el proyecto..."
          />
        </div>

        <div className="form-group">
          <label>Imágenes</label>
          <input
            type="file"
            name="images"
            multiple
            accept="image/*"
            onChange={handleFileChange}
          />
        </div>

        <div className="form-group">
          <label>Videos (MP4 / MOV)</label>
          <input
            type="file"
            name="videos"
            multiple
            accept="video/mp4,video/quicktime"
            onChange={handleFileChange}
          />
        </div>

        <div className="form-actions">
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary">
              Cancelar
            </button>
          )}

          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Creando...' : parentId ? 'Crear Caso Hijo' : 'Crear Caso Padre'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CaseFormNew;
